/**
 * 配列と比較する配列をそれぞれの値を変換する関数を使用して比較し、共通の要素を含む配列を返します。
 *
 * @template T - 配列内の値の型。
 * @template U - 比較する配列内の値の型。
 * @template V - 変換後の値の型。
 * @param items - 配列。
 * @param others - 比較する配列。
 * @param selector - 配列内の値を変換するために使用する関数。
 * @param otherSelector - 比較する配列内の値を変換するために使用する関数。
 * @returns 共通の要素を含む配列。
 */
export function intersectionBy<T, U, V>(
  items: T[],
  others: U[],
  selector: (item: T) => V,
  otherSelector: (item: U) => V,
): T[]

/**
 * 配列と比較する配列をそれぞれの値を変換する関数を使用して比較し、共通の要素を含む配列を返します。
 *
 * @template T - 配列内の値の型。
 * @template U - 比較する配列内の値の型。
 * @template V - 変換後の値の型。
 * @param others - 比較する配列。
 * @param selector - 配列内の値を変換するために使用する関数。
 * @param otherSelector - 比較する配列内の値を変換するために使用する関数。
 * @returns 配列を引数に取り、共通の要素を含む配列を返す関数。
 */
export function intersectionBy<T, U, V>(
  others: U[],
  selector: (item: T) => V,
  otherSelector: (item: U) => V,
): (items: T[]) => T[]

/**
 * @param args - 引数。
 * @returns 結果または部分適用された関数。
 */
export function intersectionBy<T, U, V>(
  ...args:
    | [T[], U[], (item: T) => V, (item: U) => V]
    | [U[], (item: T) => V, (item: U) => V]
): T[] | ((items: T[]) => T[]) {
  const intersectionByInner = (
    items: T[],
    others: U[],
    selector: (item: T) => V,
    otherSelector: (item: U) => V,
  ): T[] =>
    items.filter((item) =>
      others.some((other) => selector(item) === otherSelector(other)),
    )

  if (args.length === 3) {
    return (items: T[]) => intersectionByInner(items, ...args)
  }

  return intersectionByInner(...args)
}
